import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react'
import {
  Box,
  Typography,
  InputAdornment,
  Tooltip,
  Alert,
  Theme,
  Grid,
  InputLabel,
  MenuItem,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ClpTable from 'components/ClpTable'
import { useApi } from 'auth/useApi3'
import { IApiFunc, IApiResult } from 'auth/interface/api.interface'
import { AppContext } from 'components/AppContextProvider'
import ServiceConfirmationSidebar from '../ServiceConfirmationSidebar/ServiceConfirmationSidebar'
import { InspectionBookingSidebar } from '../InspectionBookingSidebar/InspectionBookingSidebar'
import { StyledContainer, StyledSearchBox } from './UpcomingChecksTab.styled'
import {
  ServiceOrInspection,
  isUpcomingInspection,
  isUpcomingService,
  CheckStatus,
  SwitchOption,
} from './UpcomingChecksTab.interfaces'
import {
  processUpcomingChecks,
  getLastCompletedDateValue,
  getNextDateValue,
  getLastCompletedDistanceValue,
  getNextDistanceValue,
  calculateDaysUntilService,
  calculateDistanceUntilService,
  sortByUrgency,
} from './UpcomingChecksTab.helpers'
import { apiEndpoints } from 'generated/apiEndpoints'
import { showDate } from 'components/DateHandler/dateHandler'
import { faCalendar, faCalendarCheck, faCalendarCircleExclamation, faCalendarClock, faCalendarDay, faCheckCircle, faCirclePlus, faClockRotateLeft, faPenToSquare, faWrench } from '@fortawesome/pro-solid-svg-icons'
import { CustomChipInfoProps } from 'components/ChipInfo/ChipInfo.interfaces'
import ChipInfoIcon from 'components/ChipInfo/ChipInfo'
import { useTranslation } from 'react-i18next'
import TableLink from 'components/ClpTable/components/actions/TableLink/TableLink'
import { allRoutes } from 'generated/routes'
import { DynamicTooltip } from 'components/Tooltip/DynamicTooltip'
import { StyledTableColumnSelect, StyledTableDefaultTextfield, StyledTableFormControl } from 'shared/styled/General'
import { ViewToggler } from 'components/ViewToggler/ViewToggler'
import TableButton from 'components/ClpTable/components/actions/TableButton/TableButton'
import { TableButtonTypeEnum } from 'components/ClpTable/components/actions/TableButton/tableButtonProps.interface'
import { NumberTypeSelector } from 'components/ClpTable/components/NumberTypeSelector'

function setProp<T extends Record<string, any>>(
  obj: T,
  path: string[],
  value: any
): void {
  let current: any = obj

  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {}
    }
    current = current[key]
  }

  const lastKey = path[path.length - 1]
  current[lastKey] = value
}

const getCheckStatusChip = (service: ServiceOrInspection, theme: Theme, t: any) => {
  let status: CheckStatus

  // Parse the numeric status from the service
  // The backend sends status as a numeric enum value (0=OK, 1=DueSoon, 2=Overdue)
  const numericStatus = typeof service.status === 'number'
    ? service.status
    : parseInt(service.status, 10)

  if (!isNaN(numericStatus)) {
    status = numericStatus as CheckStatus
  } else if (isUpcomingService(service) && service.intervals && service.intervals.length > 0) {
    // Fallback: Calculate max status from intervals if main status is not valid
    // Priority: Overdue (2) > DueSoon (1) > OK (0)
    const maxStatus = Math.max(
      ...service.intervals
        .map(interval => {
          const intervalStatus = typeof interval.status === 'number'
            ? interval.status
            : parseInt(String(interval.status), 10)
          return isNaN(intervalStatus) ? 0 : intervalStatus
        })
    )
    status = maxStatus as CheckStatus
  } else {
    // Final fallback to determining status from isOverdue flag
    status = service.isOverdue ? CheckStatus.Overdue : CheckStatus.OK
  }

  const bookingStatus = isUpcomingInspection(service) ? service.vehicleCheckHistoryStatus : null
  const bookingDate = isUpcomingInspection(service) ? service.inspectionBookingDate : null

  const chipConfig: CustomChipInfoProps = {}

  // Handle Booked status with special sky chip (matching Date type style)
  let statusLabel: string
  if (bookingStatus === 'Booked' && bookingDate) {
    const date = new Date(bookingDate)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    statusLabel = `${t("global.booked", "Booked")} (${day}/${month})`
    setProp(chipConfig, ['icon', 'color'], (theme.palette as any).sky[700])
    setProp(chipConfig, ['text', 'color'], (theme.palette as any).sky[700])
    setProp(chipConfig, ['chip', 'backgroundColor'], (theme.palette as any).sky[100])
    setProp(chipConfig, ['chip', 'color'], 'primary')
    setProp(chipConfig, ['border', 'color'], (theme.palette as any).sky[700])
    setProp(chipConfig, ['icon', 'source'], faClockRotateLeft)
    setProp(chipConfig, ['icon', 'color'], (theme.palette as any).sky[700])
  } else if (status === CheckStatus.Overdue) {
    statusLabel = t("global.overdue", "Overdue")
    setProp(chipConfig, ['icon', 'color'], theme.palette.error.main)
    setProp(chipConfig, ['text', 'color'], theme.palette.error.main)
    setProp(chipConfig, ['chip', 'backgroundColor'], theme.palette.error.light)
    setProp(chipConfig, ['chip', 'color'], 'error')
    setProp(chipConfig, ['border', 'color'], theme.palette.error.main)
    setProp(chipConfig, ['icon', 'source'], faCalendarCircleExclamation)
    setProp(chipConfig, ['icon', 'color'], theme.palette.error.main)
  } else if (status === CheckStatus.DueSoon) {
    statusLabel = t("global.overdue_soon", "Overdue soon")
    setProp(chipConfig, ['icon', 'color'], theme.palette.warning.main)
    setProp(chipConfig, ['text', 'color'], theme.palette.warning.main)
    setProp(chipConfig, ['chip', 'backgroundColor'], theme.palette.warning.light)
    setProp(chipConfig, ['chip', 'color'], 'warning')
    setProp(chipConfig, ['border', 'color'], theme.palette.warning.main)
    setProp(chipConfig, ['icon', 'source'], faCalendarClock)
    setProp(chipConfig, ['icon', 'color'], theme.palette.warning.main)
    // } else if (status === CheckStatus.OK) {
  } else {
    statusLabel = t("global.in_order", "In order")
    setProp(chipConfig, ['icon', 'color'], theme.palette.success.dark)
    setProp(chipConfig, ['text', 'color'], theme.palette.success.dark)
    setProp(chipConfig, ['chip', 'backgroundColor'], (theme.palette as any).success[200])
    setProp(chipConfig, ['chip', 'color'], 'warning')
    setProp(chipConfig, ['border', 'color'], theme.palette.success.dark)
    setProp(chipConfig, ['icon', 'source'], faCheckCircle)
    setProp(chipConfig, ['icon', 'color'], theme.palette.success.dark)
  }

  if (isUpcomingInspection(service) && service.hasInspectionBooking && bookingStatus === 'Waiting') {
    statusLabel = `${statusLabel} (${t("global.waiting", "Waiting")})`
  }

  setProp(chipConfig, ['text', 'data'], statusLabel)

  return (
    <ChipInfoIcon
      icon={chipConfig.icon}
      text={chipConfig.text}
      chip={chipConfig.chip}
      border={chipConfig.border}
    />
  )
}

const formatDistance = (distance?: number | string) => {
  if (distance === null || distance === undefined) return '-'
  const num = typeof distance === 'string' ? parseFloat(distance) : distance
  if (isNaN(num)) return '-'
  return `${num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} km`
}

const formatDaysUntil = (t: any, daysLeft?: number) => {
  if (daysLeft === null || daysLeft === undefined) return '-'
  if (daysLeft === -1) return `${Math.abs(daysLeft)} ${t("global.day_lower", "day")}`
  if (daysLeft < 0) return `${Math.abs(daysLeft)} ${t("checks.days_late", "days late")}`
  if (daysLeft === 0) return t("global.today", "Today")
  if (daysLeft === 1) return `1 ${t("global.day_lower", "day")}`
  return `${daysLeft} ${t("global.days", "Days")}`
}

const getColorAndWeightFromStatus = (status: CheckStatus | string | number, theme: Theme) => {
  const numericStatus = typeof status === 'number' ? status : parseInt(String(status), 10)

  if (numericStatus === CheckStatus.Overdue) {
    return {
      color: theme.palette.error.main,
      fontWeight: 600
    }
  } else if (numericStatus === CheckStatus.DueSoon) {
    return {
      color: theme.palette.warning.main,
      fontWeight: 600
    }
  } else {
    return {
      color: theme.palette.text.primary,
      fontWeight: 400
    }
  }
}

const CheckTypeChip: React.FC<{ service: ServiceOrInspection }> = ({ service }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const chipConfig: CustomChipInfoProps = {}

  if (isUpcomingInspection(service)) {
    setProp(chipConfig, ['text', 'color'], (theme.palette as any).blue[700])
    setProp(chipConfig, ['text', 'data'], t("global.inspection", "Inspection"))
  } else if (isUpcomingService(service)) {
    const intervalType = service.intervalType || t("global.service", "Service")
    setProp(chipConfig, ['text', 'color'], (theme.palette as any).orange[700])
    setProp(chipConfig, ['text', 'data'], intervalType)
  }

  return (
    <span>{chipConfig.text?.data}</span>
  )
}

const errorFallbackText = "No scheduled service or inspection checks found. Try increasing the interval or checking the vehicle's last service."
const tableDesc = `The table shows all vehicles that have <strong>active inspection intervals</strong>. Here you can track the <strong>most recently completed service, the next scheduled date</strong>, as well as the current <strong>status</strong> for each inspection. <br />Use the filters to adjust the view by days or mileage forward in time and get an overview of upcoming actions.`

const UpcomingServicesTab = ({ params, viewList, view, setView }: SwitchOption) => {
  const theme = useTheme()
  const appContext = useContext(AppContext)
  const [filteredServices, setFilteredServices] = useState<ServiceOrInspection[]>([])
  const [error, setError] = useState<string | null>(null)
  const [daysAhead, setDaysAhead] = useState("14")
  const [distanceAhead, setDistanceAhead] = useState("3500")
  const [selectedServiceIds, setSelectedServiceIds] = useState<{ vehicleId: number; checkTemplateId: number } | null>(null)
  const [selectedInspectionIds, setSelectedInspectionIds] = useState<{ vehicleId: number; checkTemplateId: number } | null>(null)
  const [upcomingChecksResult, upcomingChecksApi] = useApi() as [IApiResult, IApiFunc]
  const [upcomingVehicleChecksResult, upcomingVehicleChecksApi] = useApi() as [IApiResult, IApiFunc]
  const [showInternNumber, setShowInternNumber] = useState(false)
  const [numberType, setNumberType] = useState("reg");
  const { t } = useTranslation()

  const fetchUpcomingServices = useCallback(() => {
    if (daysAhead === "" || distanceAhead === "") return
    setError(null)
    if (params?.id) {
      const url =
        apiEndpoints.checktemplate.getupcomingchecksforvehicle.replace("{vehicleId}", String(params?.id))
        + `?daysAhead=${daysAhead}&distanceAhead=${distanceAhead}`
      upcomingChecksApi.get(url)
    } else {
      const url = `${apiEndpoints.checktemplate.getallupcomingchecks}?daysAhead=${daysAhead}&distanceAhead=${distanceAhead}`
      upcomingChecksApi.get(url)
    }
  }, [daysAhead, distanceAhead, upcomingChecksApi, params?.id])

  const handleConfirmService = useCallback((service: ServiceOrInspection) => {
    setSelectedServiceIds({
      vehicleId: service.vehicleId,
      checkTemplateId: service.checkTemplateId
    })
    appContext.toggleSideBar('ServiceConfirmationSidebar', true)
  }, [appContext])

  const handleServiceConfirmed = useCallback(() => {
    fetchUpcomingServices()
    setSelectedServiceIds(null)
  }, [fetchUpcomingServices])

  const handleBookInspection = useCallback((service: ServiceOrInspection) => {
    setSelectedInspectionIds({
      vehicleId: service.vehicleId,
      checkTemplateId: service.checkTemplateId
    })
    appContext.toggleSideBar('InspectionBookingSidebar', true)
  }, [appContext])

  const handleInspectionUpdated = useCallback(() => {
    setSelectedInspectionIds(null)
    appContext.toggleSideBar('InspectionBookingSidebar', false)
    fetchUpcomingServices()
  }, [appContext, fetchUpcomingServices])

  const handleInspectionBooked = useCallback(() => {
    fetchUpcomingServices()
    setSelectedInspectionIds(null)
  }, [fetchUpcomingServices])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUpcomingServices()
    }, 1000)

    return () => clearTimeout(timer)
  }, [daysAhead, distanceAhead, fetchUpcomingServices])

  useEffect(() => {
    if (upcomingChecksResult.status === 2) {
      const rawData = upcomingChecksResult.data as any[]
      const mergedServices = processUpcomingChecks(rawData)
      const sortedServices = sortByUrgency(mergedServices)

      setFilteredServices(sortedServices)
      setError(null)
    } else if (upcomingChecksResult.status === 3) {
      setError(t("checks.fail_load_checks", "Failed to load upcoming checks"))
    }
  }, [upcomingChecksResult])

  const columns: any[] = useMemo(() => [
    {
      accessorKey: 'checkTemplateName',
      header: t("global.name", "Name"),
      flex: 2,
      minWidth: 200,
      cell: ({ cell }: any) => (
        <Typography sx={{ fontWeight: 600 }}>
          {cell.getValue()}
        </Typography>
      ),
    },

    {
      accessorKey: 'intervalType',
      header: t("global.service_type", "Service type"),
      width: 140,
      cell: ({ cell }: any) => <CheckTypeChip service={cell.row.original} />,
    },
    {
      accessorKey: numberType === "intern" ? 'vehicleInternNumber' : 'vehicleRegNumber',
      header: numberType === "intern" ? t("global.internal_number_short", "Intern No") : t("global.reg_number_short", "Reg No"),
      width: 130,
      cell: ({ cell }: any) => {
        const row = cell.row.original
        const displayValue = numberType === "intern" ? row.vehicleInternNumber : row.vehicleRegNumber

        return cell.row.original.vehicleId ? (
          <TableLink
            label={displayValue || '-'}
            to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.vehicleId)}
          />
        ) : (
          <Typography>{displayValue || '-'}</Typography>
        )
      }
    },
    {
      accessorKey: 'status',
      header: t("global.status", "Status"),
      width: 120,
      cell: ({ cell }: any) => getCheckStatusChip(cell.row.original, theme, t),
    },
    {
      accessorKey: 'lastCompletedDateValue',
      header: t("checks.latest_checked", "Last checked"),
      width: 130,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        if (isUpcomingService(service)) {
          const lastCompleted = getLastCompletedDateValue(service)
          if (lastCompleted) return showDate(lastCompleted)
        }
        if (isUpcomingInspection(service) && service.lastCompletedValue) {
          return showDate(service.lastCompletedValue)
        }
        return '-'
      },
    },

    {
      accessorKey: 'nextDateValue',
      header: t("checks.check_latest", "Check latest"),
      width: 130,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        if (isUpcomingService(service)) {
          const nextDate = getNextDateValue(service)
          if (nextDate) return showDate(nextDate)
        } else if (isUpcomingInspection(service)) {
          if (service.nextValue) {
            return showDate(service.nextValue)
          } else if (service.earliestDueDate) {
            return showDate(service.earliestDueDate)
          }
        }
        return '-'
      },
    },
    {
      accessorKey: 'daysUntilService',
      header: t("global.days_until", "Days until"),
      textAlign: 'right',
      width: 120,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        let days: number | undefined

        if (isUpcomingService(service)) {
          days = calculateDaysUntilService(service)
        } else if (isUpcomingInspection(service)) {
          days = service.valueUntilService
        }

        if (days === null || days === undefined) return '-'

        const { color, fontWeight } = getColorAndWeightFromStatus(service.status, theme)

        return (
          <Typography style={{ color, fontWeight }}>
            {formatDaysUntil(t, days)}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'currentOdometerReading',
      header: t("global.meter_setting", "Meter setting"),
      meta: { align: 'right' },
      width: 140,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        if (isUpcomingService(service) && service.currentOdometerReading !== null && service.currentOdometerReading !== undefined) {
          return (
            <Typography sx={{ fontWeight: 500 }}>
              {formatDistance(service.currentOdometerReading)}
            </Typography>
          )
        }
        if (isUpcomingInspection(service) && service.currentOdometerReading !== null && service.currentOdometerReading !== undefined) {
          return (
            <Typography sx={{ fontWeight: 500 }}>
              {formatDistance(service.currentOdometerReading)}
            </Typography>
          )
        }
        return '-'
      },
    },
    {
      accessorKey: 'lastCompletedDistanceValue',
      header: `${t("checks.latest_distance", "Latest at")} (km)`,
      width: 130,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        if (isUpcomingService(service)) {
          const lastCompleted = getLastCompletedDistanceValue(service)
          if (lastCompleted) return formatDistance(lastCompleted)
        }
        return '-'
      },
    },
    {
      accessorKey: 'nextDistanceValue',
      header: `${t("checks.next_distance", "Next at")} (km)`,
      width: 130,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        if (isUpcomingService(service)) {
          const nextDistance = getNextDistanceValue(service)
          if (nextDistance) return formatDistance(nextDistance)
        }
        return '-'
      },
    },
    {
      accessorKey: 'distanceUntilService',
      header: `${t("global.remaining", "Remaining")} (km)`,
      width: 130,
      cell: ({ cell }: any) => {
        const service = cell.row.original
        if (!isUpcomingService(service)) return '-'

        const distance = calculateDistanceUntilService(service)
        if (distance === null || distance === undefined) return '-'

        const { color, fontWeight } = getColorAndWeightFromStatus(service.status, theme)

        return (
          <Typography style={{ color, fontWeight }}>
            {formatDistance(distance)}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'actions',
      header: '',
      width: 100,
      cell: ({ cell }: any) => {
        const check = cell.row.original

        if (isUpcomingInspection(check)) {
          const isCompleted = check.vehicleCheckHistoryStatus === 'Passed' || check.vehicleCheckHistoryStatus === 'Failed'
          const hasActiveBooking = check.hasInspectionBooking && check.vehicleCheckHistoryStatus === 'Booked'

          let tooltipTitle = t("global.book_inspection", "Book Inspection")
          if (isCompleted) {
            if (check.vehicleCheckHistoryStatus === 'Passed') {
              tooltipTitle = `${t("global.inspection", "Inspection")} ${t("global.passed", "Passed")} - ${t("checks.book_new_inspection", "Book New Inspection")}`
            } else if (check.vehicleCheckHistoryStatus === 'Failed') {
              tooltipTitle = `${t("global.inspection", "Inspection")} ${t("global.failed", "Failed")} - ${t("checks.book_new_inspection", "Book New Inspection")}`
            }
          } else if (hasActiveBooking) {
            tooltipTitle = t("checks.edit_inspectoin_booking", "Edit inspection booking")
          }

          return (
            <TableButton
              custom={{
                icon: hasActiveBooking ? faPenToSquare : faCalendarDay,
                label: hasActiveBooking ? t("checks.edit_inspectoin_booking", "Edit inspection booking") : t("global.book_inspection", "Book Inspection"),
                color: "secondary"
              }}
              toolTipText={tooltipTitle}
              type={TableButtonTypeEnum.Custom} handleOnClick={() => handleBookInspection(check)}
            />
          )
        }

        return (
          <TableButton
            custom={{
              icon: faWrench,
              label: t("checks.confirm_service", "Confirm service"),
              color: "secondary"
            }}
            toolTipText={t("checks.confirm_service", "Confirm service")}
            type={TableButtonTypeEnum.Custom} handleOnClick={() => handleConfirmService(check)}
          />
        )
      },
    },
  ], [theme, numberType, handleBookInspection, handleConfirmService, t])

  const handleRefresh = useCallback(() => {
    fetchUpcomingServices()
  }, [fetchUpcomingServices])

  const handleDaysAheadChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
  
    const value = event.target.value

    if (value === "") {
      setDaysAhead("")
    // } else if (value.startsWith("0", 0)) {
    } else if (value.startsWith("0", 0)) {
      setDaysAhead("0")
    } else {
      setDaysAhead(value)
    }
  }, [])

  const handleDistanceAheadChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {

    const value = event.target.value

    if (value === "") {
      setDistanceAhead("")
    } else if (value.startsWith("0", 0)) {
      setDistanceAhead("0")
    } else {
      setDistanceAhead(value)
    }

  }, [])

  if (error) {
    return (
      <StyledContainer>
        <Alert variant="outlined" severity="error" onClose={handleRefresh}>
          {error}
        </Alert>
      </StyledContainer>
    )
  }


  return (
    <StyledContainer>
      <StyledSearchBox pt={3.5} px={3}>
        <Box display="flex" gap={{ xs: 2, md: 1 }} justifyContent="space-between" width="100%" flexDirection={{ xs: "column", md: "row" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="labelSmall">{t("global.table_information", "Table Information")}</Typography>
            <DynamicTooltip t={t} title={t("global.table_information", "Table Information")}>
              <Grid item gap={1}>
                <Typography fontWeight="600" variant="labelSmall">
                  {t("vehicleregistry.how_to_read_control_overview", "How to read the control overview")}
                </Typography>
                <Typography sx={{ mt: 0.5 }} variant="bodySmall" color={theme.palette.grey[600]}
                  dangerouslySetInnerHTML={{
                    __html: t("checks.upcoming_table_desc", tableDesc)
                  }}
                />
              </Grid>
            </DynamicTooltip>
          </Box>
          <Box display="flex" gap={2} sx={{ flexWrap: 'wrap' }}>
            <StyledTableDefaultTextfield
              size="small"
              type="number"
              label={t("global.days_ahead", "Days Ahead")}
              value={parseInt(daysAhead, 10)}
              onChange={handleDaysAheadChange}
              sx={{ width: 120 }}
              InputProps={{
                inputProps: { min: -365, max: 365 }
              }}
            />
            <StyledTableDefaultTextfield
              size="small"
              type="number"
              label={t("global.distance_ahead", "Distance Ahead")}
              value={parseInt(distanceAhead, 10)}
              onChange={handleDistanceAheadChange}
              sx={{ width: 140 }}
              InputProps={{
                inputProps: { min: -100000, max: 100000 },
                endAdornment: <InputAdornment position="end">{t("global.km", "km")}</InputAdornment>
              }}
            />
            <NumberTypeSelector
              value={numberType}
              onChange={setNumberType}
              label={t("global.show_reg_int_nr", "Show Reg. No./Internal No.")}
              options={[
                { value: "reg", label: t("global.reg_number_short", "Reg No") },
                { value: "intern", label: t("global.internal_number_short", "Intern No") },
              ]}
            />

            <ViewToggler view={view} setView={setView} viewList={viewList} />
          </Box>
        </Box>

      </StyledSearchBox>

      <ClpTable
        rows={filteredServices}
        columns={columns}
        loadingState={upcomingChecksResult?.status}
        noRowsErrorText={t("checks.upcoming_checks_norows", errorFallbackText)}
      />

      {selectedServiceIds && (
        <ServiceConfirmationSidebar
          vehicleId={selectedServiceIds.vehicleId}
          checkTemplateId={selectedServiceIds.checkTemplateId}
          onConfirmed={handleServiceConfirmed}
        />
      )}

      {selectedInspectionIds && (
        <InspectionBookingSidebar
          vehicleId={selectedInspectionIds.vehicleId}
          checkTemplateId={selectedInspectionIds.checkTemplateId}
          onBooked={handleInspectionBooked}
          onUpdated={handleInspectionUpdated}
        />
      )}
    </StyledContainer>
  )
}

export default UpcomingServicesTab
